/**
 * 51.LA 关键事件持久 Outbox + 定时核验。
 *
 * - 接口 / 定时器 / 链上轮询等非点击事件：先入队再 dispatch
 * - sessionStorage 持久，SDK 晚到或短暂不可用时可重试
 * - status: queued → dispatched | expired
 * - 51 无回执：dispatched = 已成功调用 LA.track（非服务端 ACK）
 */

const STORAGE_KEY = 'deepsage-la-outbox-v1';
const MAX_ITEMS = 60;
const MAX_AGE_MS = 60 * 60 * 1000;
const MAX_ATTEMPTS = 40;
const SWEEP_MS = 3000;
const DISPATCHED_KEEP_MS = 10 * 60 * 1000;

/** @typedef {'queued' | 'dispatched' | 'expired'} LaOutboxStatus */

/**
 * @typedef {{
 *   id: string,
 *   key: string,
 *   eventId: string,
 *   dedupeKey?: string,
 *   at: number,
 *   status: LaOutboxStatus,
 *   attempts: number,
 *   lastAttemptAt?: number,
 *   dispatchedAt?: number,
 * }} LaOutboxItem
 */

/** @type {ReturnType<typeof setInterval> | null} */
let sweepTimer = null;
let setupDone = false;

/** @type {null | (() => boolean)} */
let sendFn = null;

function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `la-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** @returns {LaOutboxItem[]} */
export function readLaOutbox() {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.filter((item) => item && typeof item === 'object' && item.eventId);
  } catch {
    return [];
  }
}

/** @param {LaOutboxItem[]} items */
function writeLaOutbox(items) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(-MAX_ITEMS)));
  } catch {
    /* ignore quota */
  }
}

/**
 * @param {{ key: string, eventId: string, dedupeKey?: string }} input
 * @returns {LaOutboxItem}
 */
export function enqueueLaOutbox(input) {
  const key = String(input.key || '').trim();
  const eventId = String(input.eventId || '').trim();
  const dedupeKey = String(input.dedupeKey || '').trim();
  const now = Date.now();
  const items = readLaOutbox().filter((item) => {
    if (dedupeKey && item.status === 'queued' && item.dedupeKey === dedupeKey) return false;
    return true;
  });

  /** @type {LaOutboxItem} */
  const row = {
    id: createId(),
    key,
    eventId,
    at: now,
    status: 'queued',
    attempts: 0,
    ...(dedupeKey ? { dedupeKey } : {}),
  };
  items.push(row);
  writeLaOutbox(pruneOutbox(items, now));
  ensureLaOutboxSweep();
  return row;
}

/**
 * @param {LaOutboxItem[]} items
 * @param {number} now
 */
function pruneOutbox(items, now = Date.now()) {
  return items
    .filter((item) => {
      if (item.status === 'dispatched') {
        const ts = Number(item.dispatchedAt || item.at) || 0;
        return now - ts <= DISPATCHED_KEEP_MS;
      }
      if (item.status === 'expired') {
        return now - (Number(item.at) || 0) <= DISPATCHED_KEEP_MS;
      }
      return true;
    })
    .slice(-MAX_ITEMS);
}

function isLaReady() {
  if (typeof window === 'undefined') return false;
  const la = window.LA;
  return Boolean(la && (typeof la.track === 'function' || typeof la.quick === 'function'));
}

/**
 * @returns {{ flushed: number, remaining: number, ready: boolean }}
 */
export function sweepLaOutbox() {
  const now = Date.now();
  const items = readLaOutbox();
  if (!items.length) {
    stopLaOutboxSweep();
    return { flushed: 0, remaining: 0, ready: isLaReady() };
  }

  const send = sendFn;
  if (typeof send !== 'function') {
    return { flushed: 0, remaining: countQueued(items), ready: false };
  }

  let flushed = 0;
  let ready = isLaReady();

  for (const item of items) {
    if (item.status !== 'queued') continue;

    if (now - (Number(item.at) || 0) > MAX_AGE_MS) {
      item.status = 'expired';
      continue;
    }

    if (!ready) {
      item.attempts = (Number(item.attempts) || 0) + 1;
      item.lastAttemptAt = now;
      if (item.attempts >= MAX_ATTEMPTS) item.status = 'expired';
      break;
    }

    const ok = Boolean(send(item.eventId));
    item.attempts = (Number(item.attempts) || 0) + 1;
    item.lastAttemptAt = now;
    if (ok) {
      item.status = 'dispatched';
      item.dispatchedAt = now;
      flushed += 1;
      continue;
    }
    ready = false;
    if (item.attempts >= MAX_ATTEMPTS) item.status = 'expired';
    break;
  }

  writeLaOutbox(pruneOutbox(items, now));
  const remaining = countQueued(items);
  if (remaining > 0) ensureLaOutboxSweep();
  else stopLaOutboxSweep();
  return { flushed, remaining, ready };
}

function countQueued(items) {
  return items.filter((item) => item.status === 'queued').length;
}

export function ensureLaOutboxSweep() {
  if (typeof window === 'undefined') return;
  if (sweepTimer != null) return;
  sweepTimer = setInterval(() => {
    const { remaining } = sweepLaOutbox();
    if (remaining <= 0) stopLaOutboxSweep();
  }, SWEEP_MS);
}

function stopLaOutboxSweep() {
  if (sweepTimer == null) return;
  clearInterval(sweepTimer);
  sweepTimer = null;
}

/**
 * @returns {{ queued: LaOutboxItem[], dispatched: LaOutboxItem[], expired: LaOutboxItem[], all: LaOutboxItem[] }}
 */
export function snapshotLaOutbox() {
  const all = readLaOutbox();
  return {
    all,
    queued: all.filter((i) => i.status === 'queued'),
    dispatched: all.filter((i) => i.status === 'dispatched'),
    expired: all.filter((i) => i.status === 'expired'),
  };
}

/**
 * @param {{ sendEventId: (eventId: string) => boolean }} options
 */
export function setupLaOutbox(options) {
  if (setupDone || typeof window === 'undefined') return;
  setupDone = true;
  sendFn = typeof options?.sendEventId === 'function' ? options.sendEventId : null;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sweepLaOutbox();
  });

  window.addEventListener('pagehide', () => {
    // 最终再扫一次；不保证成功
    sweepLaOutbox();
  });

  // 冷启：若有残留 queued，立刻扫 + 开定时器
  const queued = countQueued(readLaOutbox());
  if (queued > 0) {
    sweepLaOutbox();
    ensureLaOutboxSweep();
  }

  if (process.env.NODE_ENV === 'development') {
    window.__dsLaOutbox = () => snapshotLaOutbox();
    window.__dsLaOutboxSweep = () => sweepLaOutbox();
  }
}
